import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';

import { ReportSite, UserSystemGroup, UserSystemGroupStrings } from 'store/storeTypes';
import { styles } from '../WorkingHours.styles';
import { ReactComponent as TshirtIcon } from 'assets/icons/tshirtIcon.svg';
import { ReactComponent as RocketIcon } from 'assets/icons/rocketIcon.svg';
import { KonnectAnalytics } from 'KonnectAnalytics/konnectAnalytics';
import {
  PRODUCTION_HOURS_SITE_SELECT_ELEMENT_ID,
  getProductionHoursSystemTypeToggleElementId,
} from 'KonnectAnalytics/analyticsElementIds';

type SystemGroupConfig = {
  key: UserSystemGroupStrings;
  icon: React.ReactElement;
};

// DTF is excluded here - Working Hours does not apply to DTF systems.
const SYSTEM_GROUP_CONFIGS: SystemGroupConfig[] = [
  { key: UserSystemGroup.Apollo, icon: <RocketIcon width={20} height={18} /> },
  { key: UserSystemGroup.DTG, icon: <TshirtIcon width={25} height={25} /> },
];

type WorkingHoursHeaderProps = {
  sites: ReportSite[];
  selectedSite: string;
  onSiteChange: (value: string) => void;
  // Multi-select "apply change to" scope - when more than one site is checked, saving
  // applies the schedule shown below to every one of them.
  selectedSites: string[];
  setSites: (sites: string[]) => void;
  applyMode: 'immediately' | 'fromDate';
  onApplyModeChange: (value: 'immediately' | 'fromDate') => void;
  startDate: string;
  onStartDateChange: (value: string) => void;
  userSystemGroup: UserSystemGroupStrings[];
  selectedSystemGroupTypes: UserSystemGroupStrings[];
  setSystemGroupTypes: (groups: UserSystemGroupStrings[]) => void;
  readOnly: boolean;
};

const WorkingHoursHeader = ({
  sites,
  selectedSite,
  onSiteChange,
  selectedSites,
  setSites,
  applyMode,
  onApplyModeChange,
  startDate,
  onStartDateChange,
  userSystemGroup,
  selectedSystemGroupTypes,
  setSystemGroupTypes,
  readOnly,
}: WorkingHoursHeaderProps) => {
  const toggleSystemGroupType = (group: UserSystemGroupStrings) => {
    const isSelected = selectedSystemGroupTypes.includes(group);
    const next = isSelected
      ? selectedSystemGroupTypes.filter(g => g !== group)
      : [...selectedSystemGroupTypes, group];
    if (isSelected && selectedSystemGroupTypes.length === 1) return; // keep at least one selected
    setSystemGroupTypes(next);
    KonnectAnalytics.productionHoursToggleSystemType({
      systemGroup: group,
      enabled: !isSelected,
      selectedSystemGroupTypes: next,
      elementId: getProductionHoursSystemTypeToggleElementId(group),
    });
  };

  const handleSitesChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const newSites = typeof value === 'string' ? value.split(',') : value;
    if (newSites.length === 0) return; // keep at least one selected
    setSites(newSites);
    // Viewing site must always be one of the checked ones.
    if (!newSites.includes(selectedSite)) {
      onSiteChange(newSites[0]);
    }
    KonnectAnalytics.productionHoursChangeSite({
      siteIds: newSites,
      elementId: PRODUCTION_HOURS_SITE_SELECT_ELEMENT_ID,
    });
  };

  const siteNameById = (id: string) => sites.find(site => site.id === id)?.name ?? id;

  const siteSelectDisplayValue = (selected: string[]) => {
    const names = selected.map(siteNameById);
    if (names.length <= 1) return names[0] ?? '';
    return `${names[0]} +${names.length - 1}`;
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  // Build YYYY-MM-DD from LOCAL date parts (avoids UTC-offset giving "yesterday")
  const toLocalIso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
      2,
      '0'
    )}`;

  const tomorrowIso = toLocalIso(tomorrow);

  // Parse stored YYYY-MM-DD as LOCAL midnight (not UTC midnight)
  const parseDateString = (s: string): Date | null => {
    if (!s) return null;
    const [year, month, day] = s.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const dateValue = parseDateString(startDate);

  // ISO YYYY-MM-DD strings compare correctly as plain strings
  const isBeforeTomorrow = (iso: string) => !iso || iso < tomorrowIso;

  const ensureStartDateNotBeforeTomorrow = () => {
    if (isBeforeTomorrow(startDate)) onStartDateChange(tomorrowIso);
  };

  const handleApplyModeChange = (value: 'immediately' | 'fromDate') => {
    const shouldUseTomorrow = value === 'fromDate' && isBeforeTomorrow(startDate);
    const effectiveStartDate = shouldUseTomorrow ? tomorrowIso : startDate;
    if (shouldUseTomorrow) {
      onStartDateChange(tomorrowIso);
    }
    onApplyModeChange(value);
    KonnectAnalytics.productionHoursChangeApplyMode({
      applyMode: value,
      startDate: effectiveStartDate,
    });
  };

  const handleDateChange = (date: Date | null) => {
    // Partial keyboard input; past dates are corrected on blur/close, not mid-typing
    if (date && Number.isNaN(date.getTime())) return;
    const nextDate = date ? toLocalIso(date) : tomorrowIso;
    onStartDateChange(nextDate);
    KonnectAnalytics.productionHoursChangeStartDate({ startDate: nextDate });
  };

  return (
    <Box sx={styles.headerWrapper}>
      <Typography variant="h5" sx={styles.pageTitle}>
        Working Hours
      </Typography>

      <Box sx={styles.controlsRow}>
        <Box sx={styles.fieldGroup}>
          <Typography sx={styles.fieldLabel}>Site</Typography>
          <Tooltip title={selectedSites.map(siteNameById).join(', ')} placement="top-start" arrow>
            <Select
              multiple
              value={selectedSites}
              size="small"
              onChange={handleSitesChange}
              renderValue={selected => siteSelectDisplayValue(selected as string[])}
              sx={styles.siteSelect}>
              {sites.map(site => (
                <MenuItem key={site.id} value={site.id}>
                  <Checkbox size="small" checked={selectedSites.includes(site.id)} />
                  <ListItemText primary={site.name} />
                </MenuItem>
              ))}
            </Select>
          </Tooltip>
        </Box>

        <Box sx={styles.fieldGroup}>
          <Typography sx={styles.fieldLabel}>Apply to system types</Typography>
          <Box sx={styles.systemFilterArea}>
            {SYSTEM_GROUP_CONFIGS.filter(cfg => userSystemGroup.includes(cfg.key)).map(cfg => (
              <Box
                key={cfg.key}
                component="button"
                sx={styles.systemGroupToggle}
                className={selectedSystemGroupTypes.includes(cfg.key) ? 'selected' : ''}
                onClick={() => toggleSystemGroupType(cfg.key)}>
                <Box sx={styles.systemGroupDot} />
                {cfg.icon}
                <Box component="span" sx={styles.systemGroupLabel}>
                  {cfg.key}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={styles.fieldGroup}>
          <Typography sx={styles.fieldLabel}>Apply change</Typography>
          <Box sx={styles.applyChangeRow}>
            <RadioGroup
              row
              value={applyMode}
              onChange={event =>
                handleApplyModeChange(event.target.value as 'immediately' | 'fromDate')
              }
              sx={styles.applyModeGroup}>
              <FormControlLabel
                value="immediately"
                disabled={readOnly}
                control={<Radio size="small" />}
                label={
                  <Tooltip title="After next process" placement="top" arrow>
                    <span>Today</span>
                  </Tooltip>
                }
              />
              <FormControlLabel
                value="fromDate"
                disabled={readOnly}
                control={<Radio size="small" />}
                label="From date"
              />
            </RadioGroup>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={dateValue}
                onChange={handleDateChange}
                onClose={ensureStartDateNotBeforeTomorrow}
                minDate={tomorrow}
                format="dd/MM/yyyy"
                disabled={readOnly || applyMode === 'immediately'}
                sx={styles.datePicker}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: false,
                    onBlur: ensureStartDateNotBeforeTomorrow,
                  },
                  popper: { sx: styles.datePickerPopper },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WorkingHoursHeader;
